import { msalInstance, silentRequest } from './msalConfig.ts';
import { classifySilentFailure, TransientStreak, type SilentFailureContext } from './silentRefreshErrors.ts';

/**
 * Session expiry recovery.
 *
 * `AuthenticatedTemplate` in App.tsx only checks that an *account* exists in the MSAL cache — and
 * with `cacheLocation: 'localStorage'` that account outlives the token by days. So an expired token
 * leaves the app looking signed in while every request 401s. This module is the missing link from
 * "the API said 401" back to "get a new token".
 *
 * Two tiers:
 *   1. Silent — `acquireTokenSilent({ forceRefresh: true })`. Uses the refresh token, no UI, no
 *      redirect. This recovers the common case (token aged out, Entra session still valid).
 *   2. Sign out — when the silent tier cannot recover, the session is declared dead and
 *      SessionExpiryGuard signs the user out to the sign-in page. Unsaved work on the page is lost;
 *      that was accepted on 2026-09-23 over the old "Reconnect" dialog, which kept the page alive
 *      but left users watching an app that looked signed in and kept failing
 *      (`tech-spec-fix-logout-on-session-expiry.md`).
 */

const RETURN_PATH_KEY = 'tagcatalog.reauth.returnTo';

export class SessionExpiredError extends Error {
  constructor(message = 'Your session has expired.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

/** True when the failure is a dead session rather than a real error worth showing the user. */
export function isSessionExpired(e: unknown): boolean {
  return e instanceof SessionExpiredError;
}

let expired = false;
const listeners = new Set<(expired: boolean) => void>();

/**
 * Flag the session as dead. Idempotent, so the eight parallel queries a detail page fires all
 * funnel into one modal instead of eight.
 */
export function markSessionExpired(): void {
  if (expired) return;
  expired = true;
  listeners.forEach(fn => fn(true));
}

export function isSessionMarkedExpired(): boolean {
  return expired;
}

export function subscribeToSessionExpiry(fn: (expired: boolean) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Outcome of a silent refresh. Only `interaction_required` is grounds for signing the user out. It
 * covers every failure that cannot recover without the user, not just MSAL's
 * `InteractionRequiredAuthError` — see `classifySilentFailure`.
 */
export type SilentRefresh =
  | { kind: 'token'; idToken: string }
  | { kind: 'interaction_required' }
  | { kind: 'transient' };

let inFlight: Promise<SilentRefresh> | null = null;
let inFlightAcquire: Promise<SilentRefresh> | null = null;

/**
 * Shared by the 401 path and the health probe, so either one succeeding resets it. Without a cap, a
 * deterministic failure that `classifySilentFailure` does not know about loops for as long as the
 * tab is open: that is how `block_iframe_reload` retried indefinitely before 2026-10-12
 * (`tech-spec-fix-silent-refresh-loop.md`).
 */
const transientStreak = new TransientStreak();

/** Context for `classifySilentFailure`: what we know about the token and the network right now. */
function failureContext(): SilentFailureContext {
  const remaining = msUntilTokenExpiry();
  return { tokenExpired: remaining !== null && remaining <= 0, online: navigator.onLine };
}

/** Turn a failed `acquireTokenSilent` into an outcome, counting it against the transient streak. */
function outcomeOfFailure(e: unknown, what: string): SilentRefresh {
  if (classifySilentFailure(e, failureContext()) === 'dead') {
    console.warn(`[auth] ${what} cannot recover; ending the session`, e);
    return { kind: 'interaction_required' };
  }
  if (transientStreak.fail()) {
    console.warn(`[auth] ${what} failed repeatedly; ending the session`, e);
    return { kind: 'interaction_required' };
  }
  console.warn(`[auth] ${what} failed (transient)`, e);
  return { kind: 'transient' };
}

/**
 * Get an id token for a request, from cache when it is still good.
 *
 * Shared across callers like `refreshTokenSilently`: when the token has died, eight parallel
 * queries must wait on one renewal, not eight. And the outcome is returned rather than swallowed,
 * so `request()` can end the session now instead of sending an unauthenticated request, taking the
 * 401, and paying for a second renewal attempt.
 */
export function acquireTokenForRequest(): Promise<SilentRefresh> {
  inFlightAcquire ??= (async (): Promise<SilentRefresh> => {
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) return { kind: 'interaction_required' };
    try {
      const result = await msalInstance.acquireTokenSilent({ ...silentRequest, account });
      transientStreak.success();
      return { kind: 'token', idToken: result.idToken };
    } catch (e) {
      return outcomeOfFailure(e, 'silent token acquisition');
    }
  })().finally(() => { inFlightAcquire = null; });
  return inFlightAcquire;
}

/**
 * Attempt a no-UI token refresh.
 *
 * `forceRefresh` matters: without it MSAL can hand back the same cached token that just 401'd,
 * turning the retry into a guaranteed second failure.
 *
 * Shared across callers: a detail page fires several requests at once, and when the token dies they
 * all 401 together. Without this each one ran its own refresh, which is the burst of repeated
 * reconnect attempts users saw before anything told them the session was gone.
 *
 * `transient` (offline, Entra 5xx) is kept apart from `interaction_required` because the caller now
 * signs the user out on the latter, and signing someone out over a network blip costs them their
 * page for nothing. But only up to `MAX_TRANSIENT_STREAK` in a row: past that it is not a blip.
 */
export function refreshTokenSilently(): Promise<SilentRefresh> {
  inFlight ??= (async (): Promise<SilentRefresh> => {
    const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
    if (!account) return { kind: 'interaction_required' };
    try {
      const result = await msalInstance.acquireTokenSilent({ ...silentRequest, account, forceRefresh: true });
      transientStreak.success();
      return { kind: 'token', idToken: result.idToken };
    } catch (e) {
      return outcomeOfFailure(e, 'silent token refresh');
    }
  })().finally(() => { inFlight = null; });
  return inFlight;
}

/**
 * Sign out after the session has died, landing on the sign-in page.
 *
 * Local only: `clearCache` drops this app's MSAL account and tokens, and the reload renders
 * `UnauthenticatedTemplate`'s Login page. Deliberately not `logoutRedirect` — that ends the Entra
 * browser session, signing the user out of every other Microsoft app in the browser over what is
 * this app's token problem, and it costs a round trip to Entra's logout page first.
 *
 * The current URL is saved so signing in again returns the user to where they were.
 */
export async function signOutExpiredSession(): Promise<void> {
  sessionStorage.setItem(RETURN_PATH_KEY, window.location.pathname + window.location.search + window.location.hash);
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  try {
    await msalInstance.clearCache(account ? { account } : undefined);
  } catch (e) {
    // Reload regardless: a half-cleared cache still fails its next silent call and lands here again,
    // which beats leaving the user on a page that cannot load anything.
    console.error('[auth] clearing the expired session failed', e);
  }
  window.location.assign(window.location.origin);
}

/** True when this page load is the sign-in page shown after an expired session was signed out. */
export function wasSignedOutByExpiry(): boolean {
  return sessionStorage.getItem(RETURN_PATH_KEY) !== null;
}

/**
 * Read and clear the path saved when an expired session was signed out. Call it only on a load that
 * is returning from a sign-in redirect — the sign-out reload itself must leave the path in place.
 * Returns null when there is no saved path, or when it is where we already are.
 */
export function consumeReturnPath(): string | null {
  const saved = sessionStorage.getItem(RETURN_PATH_KEY);
  sessionStorage.removeItem(RETURN_PATH_KEY);
  if (!saved) return null;
  const current = window.location.pathname + window.location.search + window.location.hash;
  return saved === current ? null : saved;
}

/**
 * How far ahead of the id token's `exp` a proactive check starts trying to renew.
 *
 * Matched to MSAL's own renewal offset (300s): a token this close to expiry makes
 * `acquireTokenSilent` attempt a real renewal rather than return from cache, so the probe finds out
 * whether the Entra session is still alive at the earliest moment it can — up to five minutes
 * before any request would have 401'd.
 */
const EXPIRY_SKEW_MS = 5 * 60 * 1000;

/** Milliseconds until the cached id token expires. Null when there is no token to read. */
export function msUntilTokenExpiry(): number | null {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  const exp = account?.idTokenClaims?.exp;
  if (typeof exp !== 'number') return null;
  return exp * 1000 - Date.now();
}

/**
 * Proactive counterpart to the 401 recovery in `services/api.ts`.
 *
 * That path can only fire when a request happens to go out, so the modal used to appear after an
 * arbitrary number of clicks — a page served entirely from the React Query cache makes no request
 * at all, and an idle tab makes none for as long as it sits there. This asks the question directly.
 *
 * Deliberately **not** `forceRefresh`: while the token is comfortably valid the check is a claim
 * read with no network cost, and MSAL only reaches out to Entra once `exp` is near — which is
 * exactly when the answer becomes interesting. A failure `classifySilentFailure` calls dead kills
 * the session; anything else (offline, Entra 5xx) is transient and gets retried on the next tick,
 * because destroying a live session over a network blip costs the user their page. A run of
 * `MAX_TRANSIENT_STREAK` transient failures kills it too.
 */
export async function checkSessionHealth(): Promise<void> {
  if (expired) return;
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return; // UnauthenticatedTemplate owns this case.

  const remaining = msUntilTokenExpiry();
  if (remaining !== null && remaining > EXPIRY_SKEW_MS) return;

  try {
    await msalInstance.acquireTokenSilent({ ...silentRequest, account });
    transientStreak.success();
  } catch (e) {
    if (classifySilentFailure(e, failureContext()) === 'dead' || transientStreak.fail()) {
      console.warn('[auth] session health probe cannot recover; ending the session', e);
      markSessionExpired();
    } else {
      console.warn('[auth] session health probe failed (transient)', e);
    }
  }
}
